import { MedChronEventSchema } from '@/medical-chronology/events/store/schemas'
import { MedChronReferenceSchema } from '@/medical-chronology/references/store/schemas'
import { parseISODate, parseTimestamp } from '@/util/zod/validators'
import { z } from 'zod'

// Mirrors backend paxton_app/med_chron/asset/schemas.py + the update/file status enums.

export enum MedChronUpdateKind {
  INITIALIZE = 'INITIALIZE',
  ADD_FILES = 'ADD_FILES',
  REMOVE_FILE = 'REMOVE_FILE',
}

// PENDING -> EXTRACTING -> MERGING -> FINALIZING -> DONE is the happy path; FAILED_FILES holds
// the run until the user retries or completes excluding failures; ERROR left the live
// chronology untouched.
export enum MedChronUpdateStatus {
  PENDING = 'PENDING',
  EXTRACTING = 'EXTRACTING',
  MERGING = 'MERGING',
  FINALIZING = 'FINALIZING',
  DONE = 'DONE',
  FAILED_FILES = 'FAILED_FILES',
  ERROR = 'ERROR',
}

export const kActiveMedChronUpdateStatuses = [
  MedChronUpdateStatus.PENDING,
  MedChronUpdateStatus.EXTRACTING,
  MedChronUpdateStatus.MERGING,
  MedChronUpdateStatus.FINALIZING,
]

export enum MedChronAssetFileStatus {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  EXTRACTED = 'EXTRACTED',
  INCLUDED = 'INCLUDED',
  FAILED = 'FAILED',
}

export enum FileAddDisposition {
  NEW = 'NEW',
  ALREADY_INCLUDED = 'ALREADY_INCLUDED',
  FAILED_WILL_RETRY = 'FAILED_WILL_RETRY',
  NOT_READY = 'NOT_READY',
  NOT_IN_MATTER = 'NOT_IN_MATTER',
}

export const MedChronUpdateReadSchema = z.object({
  id: z.string().uuid(),
  asset_id: z.string().uuid(),
  user_id: z.string(),
  kind: z.nativeEnum(MedChronUpdateKind),
  status: z.nativeEnum(MedChronUpdateStatus),
  requested_file_ids: z.array(z.string().uuid()).default([]),
  removed_file_id: z.string().uuid().nullable(),
  files_added: z.number().int().nullable(),
  events_added: z.number().int().nullable(),
  events_removed: z.number().int().nullable(),
  files_unchanged: z.number().int().nullable(),
  files_failed: z.number().int().nullable(),
  error_reason: z.string().nullable(),
  workflow_version: z.number().int().nullable(),
  completed_at: z.preprocess((val, ctx) => parseTimestamp(val, ctx), z.number().nullable()),
  created_at: z.preprocess((val, ctx) => parseTimestamp(val, ctx), z.number()),
  updated_at: z.preprocess((val, ctx) => parseTimestamp(val, ctx), z.number()),
})
export type MedChronUpdateRead = z.infer<typeof MedChronUpdateReadSchema>

export const MedChronUpdateHistoryResponseSchema = z.object({
  asset_id: z.string().uuid(),
  updates: z.array(MedChronUpdateReadSchema),
})
export type MedChronUpdateHistoryResponse = z.infer<typeof MedChronUpdateHistoryResponseSchema>

export const MedChronAssetOverviewSchema = z.object({
  id: z.string().uuid(),
  matter_id: z.string().uuid(),
  chronology_version: z.number().int(),
  workflow_version: z.number().int().nullable(),
  last_built_at: z.preprocess((val, ctx) => parseTimestamp(val, ctx), z.number().nullable()),
  total_events: z.number().int(),
  total_files: z.number().int(),
  active_update: MedChronUpdateReadSchema.nullable(),
  created_at: z.preprocess((val, ctx) => parseTimestamp(val, ctx), z.number()),
  updated_at: z.preprocess((val, ctx) => parseTimestamp(val, ctx), z.number()),
})
export type MedChronAssetOverview = z.infer<typeof MedChronAssetOverviewSchema>

// Get-or-empty response: asset is null when the matter has no chronology yet.
export const MedChronAssetOverviewResponseSchema = z.object({
  asset: MedChronAssetOverviewSchema.nullable(),
})
export type MedChronAssetOverviewResponse = z.infer<typeof MedChronAssetOverviewResponseSchema>

export const MedChronAssetEventsResponseSchema = z.object({
  asset_id: z.string().uuid(),
  chronology_version: z.number().int(),
  total_events: z.number().int(),
  events: z.array(MedChronEventSchema),
  references: z.record(z.string().uuid(), MedChronReferenceSchema),
})
export type MedChronAssetEventsResponse = z.infer<typeof MedChronAssetEventsResponseSchema>

export const MedChronAssetFileViewSchema = z.object({
  user_file_id: z.string().uuid(),
  display_name: z.string(),
  status: z.nativeEnum(MedChronAssetFileStatus),
  inclusion_index: z.number().int(),
  last_processed_at: z.preprocess((val, ctx) => parseTimestamp(val, ctx), z.number().nullable()),
  events_extracted: z.number().int(),
  error_reason: z.string().nullable(),
})
export type MedChronAssetFileView = z.infer<typeof MedChronAssetFileViewSchema>

export const MedChronAssetFilesResponseSchema = z.object({
  asset_id: z.string().uuid(),
  files: z.array(MedChronAssetFileViewSchema),
})
export type MedChronAssetFilesResponse = z.infer<typeof MedChronAssetFilesResponseSchema>

export const PossibleDuplicateSchema = z.object({
  user_file_id: z.string().uuid(),
  display_name: z.string(),
})
export type PossibleDuplicate = z.infer<typeof PossibleDuplicateSchema>

export const FileAddPreviewItemSchema = z.object({
  user_file_id: z.string().uuid(),
  display_name: z.string().nullable(),
  disposition: z.nativeEnum(FileAddDisposition),
  possible_duplicate_of: PossibleDuplicateSchema.nullable(),
})
export type FileAddPreviewItem = z.infer<typeof FileAddPreviewItemSchema>

export const AddFilesPreviewResponseSchema = z.object({
  asset_id: z.string().uuid(),
  items: z.array(FileAddPreviewItemSchema),
})
export type AddFilesPreviewResponse = z.infer<typeof AddFilesPreviewResponseSchema>

export const RemovalImpactResponseSchema = z.object({
  user_file_id: z.string().uuid(),
  display_name: z.string(),
  events_count: z.number().int(),
  earliest_date_of_service: z.preprocess((val, ctx) => parseISODate(val, ctx), z.string().nullable()),
  latest_date_of_service: z.preprocess((val, ctx) => parseISODate(val, ctx), z.string().nullable()),
})
export type RemovalImpactResponse = z.infer<typeof RemovalImpactResponseSchema>

export const RemoveFileResponseSchema = z.object({
  asset_id: z.string().uuid(),
  user_file_id: z.string().uuid(),
  events_removed: z.number().int(),
  total_events: z.number().int(),
  total_files: z.number().int(),
  chronology_version: z.number().int(),
})
export type RemoveFileResponse = z.infer<typeof RemoveFileResponseSchema>

export const InitializeChronologyResponseSchema = z.object({
  asset: MedChronAssetOverviewSchema,
  update: MedChronUpdateReadSchema,
})
export type InitializeChronologyResponse = z.infer<typeof InitializeChronologyResponseSchema>

export const ApplyUpdateResponseSchema = z.object({
  update: MedChronUpdateReadSchema,
})
export type ApplyUpdateResponse = z.infer<typeof ApplyUpdateResponseSchema>

export const UpdateProgressSchema = z.object({
  files_total: z.number().int(),
  files_pending: z.number().int(),
  files_processing: z.number().int(),
  files_extracted: z.number().int(),
  files_included: z.number().int(),
  files_failed: z.number().int(),
})
export type UpdateProgress = z.infer<typeof UpdateProgressSchema>

export const FailedFileSummarySchema = z.object({
  user_file_id: z.string().uuid(),
  display_name: z.string(),
  error_reason: z.string().nullable(),
})
export type FailedFileSummary = z.infer<typeof FailedFileSummarySchema>

// update.id doubles as the user-facing error ID when the run ends in ERROR.
export const UpdateStatusResponseSchema = z.object({
  update: MedChronUpdateReadSchema,
  progress: UpdateProgressSchema,
  failed_files: z.array(FailedFileSummarySchema),
})
export type UpdateStatusResponse = z.infer<typeof UpdateStatusResponseSchema>

// Request payloads (validated server-side; plain types here).
export type InitializeChronologyRequest = {
  matter_id: string
  user_file_ids: string[]
}

export type AddFilesPreviewRequest = {
  user_file_ids: string[]
}

export type ApplyUpdateRequest = {
  add_user_file_ids: string[]
}
